import { useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar, Clock, MapPin, Trash2, Plus, CheckCircle2, XCircle, AlertCircle, Phone } from "lucide-react";
import { toast } from "sonner";

const DAYS = ["E hënë", "E martë", "E mërkurë", "E enjte", "E premte", "E shtunë", "E diel"];

export default function TeacherConsultations() {
    const [data, setData] = useState({ slots: [], minutes_per_week: 0, minutes_per_month_estimate: 0, meets_monthly_minimum: false });
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ day_of_week: 1, start_time: "14:00", end_time: "15:00", location: "", note: "" });

    const refresh = async () => {
        setLoading(true);
        try {
            const [r1, r2] = await Promise.all([
                api.get("/teacher/consultation-slots"),
                api.get("/teacher/consultation-bookings"),
            ]);
            setData(r1.data);
            setBookings(r2.data);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        } finally { setLoading(false); }
    };
    useEffect(() => { refresh(); }, []);

    const addSlot = async (e) => {
        e.preventDefault();
        try {
            await api.post("/teacher/consultation-slot", form);
            toast.success("Slot-i u shtua");
            setForm({ day_of_week: 1, start_time: "14:00", end_time: "15:00", location: "", note: "" });
            refresh();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        }
    };

    const removeSlot = async (id) => {
        if (!window.confirm("Të heqësh këtë slot?")) return;
        try {
            await api.delete(`/teacher/consultation-slot/${id}`);
            refresh();
        } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    };

    const setStatus = async (bid, status) => {
        try {
            await api.patch(`/teacher/consultation-booking/${bid}`, { status });
            toast.success(status === "confirmed" ? "U konfirmua" : "U anulua");
            refresh();
        } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    };

    const upcoming = useMemo(() => {
        const today = new Date().toISOString().slice(0, 10);
        return bookings.filter((b) => b.date >= today && b.status !== "cancelled").sort((a, b) => (a.date + a.start_time).localeCompare(b.date + b.start_time));
    }, [bookings]);
    const past = useMemo(() => {
        const today = new Date().toISOString().slice(0, 10);
        return bookings.filter((b) => b.date < today || b.status === "cancelled");
    }, [bookings]);

    return (
        <Layout>
            <div className="mb-6">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Konsultimet me prindër</div>
                <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 tracking-tight">Konsultat e mia</h1>
                <p className="text-slate-600 mt-2 text-sm">Cakto orarin javor — prindërit do të rezervojnë slote 30-min nga këto blloqe</p>
            </div>

            {/* Monthly minimum gauge */}
            <div className={`mb-5 rounded-2xl border p-4 flex items-center gap-4 ${data.meets_monthly_minimum ? "bg-sage/5 border-sage/30" : "bg-amber-50 border-amber-300"}`} data-testid="monthly-minimum-gauge">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${data.meets_monthly_minimum ? "bg-sage text-white" : "bg-amber-500 text-white"}`}>
                    {data.meets_monthly_minimum ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                </div>
                <div className="flex-1">
                    <div className="text-sm font-bold text-slate-800">
                        {data.minutes_per_week} min/javë • ~{data.minutes_per_month_estimate} min/muaj
                    </div>
                    <div className="text-xs text-slate-600 mt-0.5">
                        {data.meets_monthly_minimum
                            ? "✓ E plotëson minimumin prej 1 ore në muaj për takime me prindër"
                            : "Caktoni së paku 60 min/muaj sipas politikës së shkollës"}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* New slot form */}
                <form onSubmit={addSlot} className="lg:col-span-1 bg-white border border-slate-200 rounded-2xl p-5" data-testid="new-slot-form">
                    <div className="flex items-center gap-2 mb-4">
                        <Plus className="w-4 h-4 text-oxford" />
                        <h2 className="font-heading text-lg font-bold text-slate-900">Shto bllok kohor</h2>
                    </div>
                    <label className="block text-xs font-bold text-slate-600 mb-1.5 mt-3">Dita e javës</label>
                    <select value={form.day_of_week} onChange={(e) => setForm({ ...form, day_of_week: parseInt(e.target.value) })} className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm" data-testid="form-dow">
                        {DAYS.slice(0, 5).map((d, i) => <option key={i} value={i}>{d}</option>)}
                    </select>
                    <div className="grid grid-cols-2 gap-2 mt-3">
                        <div>
                            <label className="block text-xs font-bold text-slate-600 mb-1.5">Fillimi</label>
                            <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="form-start" />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-600 mb-1.5">Mbarimi</label>
                            <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="form-end" />
                        </div>
                    </div>
                    <label className="block text-xs font-bold text-slate-600 mb-1.5 mt-3">Vendi (opsionale)</label>
                    <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="p.sh., Zyra 12 ose link Zoom" data-testid="form-location" />
                    <label className="block text-xs font-bold text-slate-600 mb-1.5 mt-3">Shënim (opsionale)</label>
                    <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="p.sh., Vetëm me rezervim paraprak" data-testid="form-note" />
                    <Button type="submit" className="mt-4 w-full bg-oxford hover:bg-oxfordhover" data-testid="form-submit">
                        Shto bllokun
                    </Button>
                    <p className="text-[11px] text-slate-500 mt-3">Sistemi ndan automatikisht në slote 30-min që mund të rezervohen</p>
                </form>

                {/* Existing slots */}
                <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl overflow-hidden">
                    <div className="px-4 sm:px-5 py-3 border-b border-slate-200 bg-bone/40 flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-oxford" />
                        <div className="text-xs uppercase tracking-widest font-bold text-slate-700">Orari javor i konsultimit</div>
                        <div className="ml-auto text-xs text-slate-500">{data.slots.length} bllok(e)</div>
                    </div>
                    {loading ? (
                        <div className="p-8 text-center text-sm text-slate-500">Duke ngarkuar…</div>
                    ) : data.slots.length === 0 ? (
                        <div className="p-8 text-center text-sm text-slate-500">Asnjë bllok i caktuar — shtoni një bllok kohor në të majtë</div>
                    ) : (
                        <div className="divide-y divide-slate-200">
                            {data.slots.map((s) => (
                                <div key={s.id} className="px-4 sm:px-5 py-3 flex items-center gap-3" data-testid={`slot-${s.id}`}>
                                    <div className="w-12 h-12 rounded-xl bg-oxford/10 text-oxford flex flex-col items-center justify-center font-bold text-[10px] uppercase tracking-wider shrink-0">
                                        {s.day_label.slice(2, 6)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-bold text-slate-900 text-sm">{s.day_label} • {s.start_time}–{s.end_time}</div>
                                        <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
                                            <span><Clock className="inline w-3 h-3 mr-0.5" /> {s.subslots_count} slote 30-min</span>
                                            {s.location && <span><MapPin className="inline w-3 h-3 mr-0.5" /> {s.location}</span>}
                                        </div>
                                    </div>
                                    <button onClick={() => removeSlot(s.id)} className="p-2 rounded-md hover:bg-red-50 text-slate-400 hover:text-red-600" title="Hiq" data-testid={`slot-delete-${s.id}`}>
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Upcoming bookings */}
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mt-5">
                <div className="px-4 sm:px-5 py-3 border-b border-slate-200 bg-bone/40 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-sage" />
                    <div className="text-xs uppercase tracking-widest font-bold text-slate-700">Rezervime të ardhshme</div>
                    <div className="ml-auto text-xs text-slate-500">{upcoming.length}</div>
                </div>
                {upcoming.length === 0 ? (
                    <div className="p-8 text-center text-sm text-slate-500" data-testid="no-bookings">Asnjë rezervim aktiv</div>
                ) : (
                    <div className="divide-y divide-slate-200">
                        {upcoming.map((b) => (
                            <div key={b.id} className="px-4 sm:px-5 py-3 flex items-center gap-3" data-testid={`booking-${b.id}`}>
                                <div className="w-14 text-center shrink-0">
                                    <div className="text-[10px] text-slate-500 uppercase font-bold">{b.date.slice(5)}</div>
                                    <div className="font-mono font-bold text-slate-800 text-sm">{b.start_time}</div>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-slate-900 text-sm">{b.parent_name}</div>
                                    <div className="text-[11px] text-slate-500">Prindi i {b.student_name}{b.topic && ` • ${b.topic}`}</div>
                                </div>
                                <span className={`px-2 py-1 rounded-full text-[10px] font-bold border ${b.status === "confirmed" ? "bg-sage/10 text-sage border-sage/30" : b.status === "cancelled" ? "bg-slate-100 text-slate-500 border-slate-200" : "bg-amber-50 text-amber-700 border-amber-300"}`}>
                                    {b.status === "confirmed" ? "Konfirmuar" : b.status === "cancelled" ? "Anulluar" : "Pritur"}
                                </span>
                                {b.parent_phone && (
                                    <a href={`tel:${b.parent_phone}`} className="p-2 rounded-md text-sage hover:bg-sage/10" title="Thirr">
                                        <Phone className="w-4 h-4" />
                                    </a>
                                )}
                                {b.status !== "confirmed" && b.status !== "cancelled" && (
                                    <button onClick={() => setStatus(b.id, "confirmed")} className="p-2 rounded-md text-sage hover:bg-sage/10" title="Konfirmo" data-testid={`confirm-${b.id}`}>
                                        <CheckCircle2 className="w-4 h-4" />
                                    </button>
                                )}
                                {b.status !== "cancelled" && (
                                    <button onClick={() => setStatus(b.id, "cancelled")} className="p-2 rounded-md text-red-500 hover:bg-red-50" title="Anulo" data-testid={`cancel-${b.id}`}>
                                        <XCircle className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {past.length > 0 && (
                <details className="mt-4">
                    <summary className="text-xs font-semibold text-slate-500 cursor-pointer">Historiku ({past.length})</summary>
                    <div className="bg-white/60 border border-slate-200 rounded-xl mt-2 divide-y divide-slate-200">
                        {past.map((b) => (
                            <div key={b.id} className="px-4 py-2 text-xs flex items-center gap-3 text-slate-500">
                                <div className="font-mono w-24">{b.date} {b.start_time}</div>
                                <div className="flex-1">{b.parent_name} ({b.student_name})</div>
                                <div className="text-[10px] uppercase">{b.status}</div>
                            </div>
                        ))}
                    </div>
                </details>
            )}
        </Layout>
    );
}
